import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { IconBed, IconPen, OrangeButton, SmallButton } from 'components';
import { COLORS } from 'utils/colors';
import SingleBed from './singleBed';
import { updateBeds, releaseSystemBeds } from 'api/api';
import { CLIENT_STATUS } from 'api/constants';
import { useUser } from 'context/userContext';

export const Beds = ({ partner }) => {
  const { updateUserDetails } = useUser();
  const [isInEditMode, setIsInEditMode] = useState(false);
  const { beds, bed_count, id: partner_id } = partner;
  const [totalBeds, setTotalBeds] = useState(bed_count);
  const [bedStatuses, setBedStatuses] = useState(
    Array.from({ length: bed_count }, (_, index) => index + 1 <= beds.length),
  );

  useEffect(() => {
    setTotalBeds(bed_count);
    setBedStatuses(Array.from({ length: bed_count }, (_, index) => index + 1 <= beds.length));
  }, [bed_count, beds]);

  const occupiedBeds = bedStatuses.filter((status) => status).length;
  const availableBeds = totalBeds - occupiedBeds;

  beds.sort((a, b) => {
    if (a.status === CLIENT_STATUS.in_progress && b.status !== CLIENT_STATUS.in_progress) return -1;
    if (a.status !== CLIENT_STATUS.in_progress && b.status === CLIENT_STATUS.in_progress) return 1;
    return 0;
  });

  const handleBedToggle = useCallback(
    (index, client) => {
      if (client) {
        releaseSystemBeds(partner_id, client.user_id, () => {
          const updatedStatuses = [...bedStatuses];
          updatedStatuses[index] = !updatedStatuses[index];
          setBedStatuses(updatedStatuses.sort((a, b) => b - a));
          updateUserDetails();
        });
      } else {
        const updatedStatuses = [...bedStatuses];
        updatedStatuses[index] = !updatedStatuses[index];
        setBedStatuses(updatedStatuses);
      }
    },
    [bedStatuses, partner_id, updateUserDetails],
  );

  const handleSaveEdit = () => {
    if (isInEditMode) {
      const bedData = {
        total_beds_count: totalBeds,
        manual_occupied_count:
          occupiedBeds -
          beds.filter(
            (bed) =>
              bed.system &&
              bed.status !== CLIENT_STATUS.completed &&
              bed.status !== CLIENT_STATUS.failed,
          ).length,
      };

      updateBeds(bedData, partner_id, (data) => {
        setTotalBeds(data.bed_count);
        setBedStatuses(bedStatuses.sort((a, b) => b - a));
        updateUserDetails();
        return setIsInEditMode(false);
      });
    }

    setIsInEditMode(true);
  };

  const storeBeds = useMemo(() => {
    return Array.from({ length: totalBeds }).map((_, index) => {
      return (
        <SingleBed
          key={`bed-${index}`}
          isBedOccupied={bedStatuses[index]}
          toggleBed={(client) => handleBedToggle(index, client)}
          inEdit={isInEditMode}
          client={index < beds.length ? beds[index] : null}
          isPWClient={bedStatuses[index] && beds[index]?.system}
        />
      );
    });
  }, [totalBeds, bedStatuses, isInEditMode, beds, handleBedToggle]);

  return (
    <div className="flex flex-col base-block pt-[30px] flex-1">
      <div className="flex items-center justify-between px-[30px] mb-[17px]">
        <p className="nunito-title">Beds</p>
        <div className="flex items-stretch gap-x-4">
          {isInEditMode && (
            <SmallButton
              btnClassName="!h-[44px] !px-6"
              label="Cancel"
              onClick={() => setIsInEditMode(false)}
            />
          )}
          <OrangeButton
            icon={!isInEditMode && <IconPen />}
            label={isInEditMode ? 'Save' : 'Edit'}
            onClick={handleSaveEdit}
          />
        </div>
      </div>
      <div
        className={classNames(
          'relative w-full flex items-start pt-8 pb-[30px] gap-x-2.5 border-t border-color-lightBlue px-[30px]',
          'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-lightBlue before:bg-white before:z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
        )}
      >
        <div className="flex flex-col gap-y-6">
          {isInEditMode ? (
            <div className="flex flex-col gap-y-1">
              <p className="text-14 font-medium">Beds Count*</p>
              <div className="flex items-center gap-x-1">
                <button
                  onClick={() => setTotalBeds(+totalBeds - 1)}
                  disabled={totalBeds <= occupiedBeds}
                  className={`w-10 h-10 shadow-block-shadow text-center rounded-ten ${totalBeds <= 1 ? 'opacity-50' : ''}`}
                >
                  -
                </button>
                <input
                  onChange={(e) => setTotalBeds(e.target.value)}
                  type="number"
                  className="w-10 h-10 border border-divider outline-divider outline-1 text-center rounded-ten text-14"
                  value={totalBeds}
                  readOnly
                />
                <button
                  onClick={() => setTotalBeds(+totalBeds + 1)}
                  className="w-10 h-10 shadow-block-shadow text-center rounded-ten"
                >
                  +
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-x-4">
              <div className="bg-yellow rounded-full w-[84px] h-[84px] flex items-center justify-center">
                <IconBed className="w-10 h-10 pointer-events-none" color={COLORS.orange} />
              </div>
              <div className="flex flex-col gap-y-1">
                <p className="text-14 text-lightBlue">Total</p>
                <p className="text-24">{totalBeds}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-x-4">
            <div className="bg-pink rounded-full w-[84px] h-[84px] flex items-center justify-center">
              <IconBed className="w-10 h-10 pointer-events-none" color={COLORS.red} />
            </div>
            <div className="flex flex-col gap-y-1">
              <p className="text-14 text-lightBlue">Occupied</p>
              <p className="text-24">{occupiedBeds}</p>
            </div>
          </div>
          <div className="flex items-center gap-x-4">
            <div className="bg-lightGreen rounded-full w-[84px] h-[84px] flex items-center justify-center">
              <IconBed className="w-10 h-10 pointer-events-none" color={COLORS.green} />
            </div>
            <div className="flex flex-col gap-y-1">
              <p className="text-14 text-lightBlue">Available</p>
              <p className="text-24">{availableBeds}</p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 max-h-[328px] scrollbar scrollbar-thumb-sky-700 scrollbar-track-sky-300  overflow-y-auto">
          {storeBeds}
        </div>
      </div>
    </div>
  );
};
